import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { FetchError } from 'src/types/TApiQueries';
import {
  AiSettings,
  AiBudgetResponse,
  AiUserBudget,
  AiCreditsResponse,
  AiCreditLedgerResponse,
  AiUsageBreakdownResponse,
} from 'src/types/TAiSettings';
import {
  getAiSettings,
  getAiBudget,
  getUserAiBudget,
  getAiCredits,
  getAiCreditLedger,
  getAiUsageBreakdown,
} from '../functions/aiSettingsApi';
import { queryNames } from '../utils/queryNames';

// Boot-time race with the backend's /stats/* rate limiter can 429 the AI
// budget/credits requests on first paint of /usage. Retry only on those
// transient failures so we self-heal without looping on 404 (endpoint
// missing) or 401/403 (permission denied). Callers can still override.
const retryOnTransient = (failureCount: number, error: FetchError): boolean => {
  if (failureCount >= 2) return false;
  const status = (error as { status?: number } | null)?.status;
  if (typeof status !== 'number') return false;
  return status === 429 || (status >= 500 && status < 600);
};

export function useAiSettingsQuery(queryOptions?: UseQueryOptions<AiSettings, FetchError>) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getAiSettings(accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<AiSettings, FetchError>([queryNames.aiSettings], queryFn, {
    ...queryOptions,
  });
}

export function useAiBudgetQuery(queryOptions?: UseQueryOptions<AiBudgetResponse, FetchError>) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getAiBudget(accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [accessToken]);

  return useQuery<AiBudgetResponse, FetchError>([queryNames.aiBudget], queryFn, {
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: retryOnTransient,
    ...queryOptions,
  });
}

export function useAiCreditsQuery(
  period: string,
  queryOptions?: UseQueryOptions<AiCreditsResponse, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    const result = (await getAiCredits(period, accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [period, accessToken]);

  return useQuery<AiCreditsResponse, FetchError>([queryNames.aiCredits, period], queryFn, {
    retry: retryOnTransient,
    ...queryOptions,
  });
}

export function useAiCreditLedgerQuery(
  opts?: { limit?: number; groupId?: number },
  queryOptions?: UseQueryOptions<AiCreditLedgerResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const limit = opts?.limit;
  const groupId = opts?.groupId;

  const fetchFn = async () => {
    const result = (await getAiCreditLedger(accessToken, { limit, groupId })) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(fetchFn),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, limit, groupId],
  );

  return useQuery<AiCreditLedgerResponse, FetchError>(
    [queryNames.aiCreditLedger, limit ?? 'default', groupId ?? 'self'],
    queryFn,
    {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Default: do NOT retry 403/404 — they're authoritative.
      retry: retryOnTransient,
      ...queryOptions,
    },
  );
}

export function useAiUsageBreakdownQuery(
  opts?: { groupId?: number; period?: number },
  queryOptions?: UseQueryOptions<AiUsageBreakdownResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const groupId = opts?.groupId;
  const period = opts?.period;

  const fetchFn = async () => {
    const result = (await getAiUsageBreakdown(accessToken, { groupId, period })) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(fetchFn),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, groupId, period],
  );

  return useQuery<AiUsageBreakdownResponse, FetchError>(
    [queryNames.aiUsageBreakdown, groupId ?? 'self', period ?? 'default'],
    queryFn,
    {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: retryOnTransient,
      ...queryOptions,
    },
  );
}

export function useUserAiBudgetQuery(
  userId: number | undefined,
  queryOptions?: UseQueryOptions<AiUserBudget, FetchError>,
) {
  const accessToken = useAccessToken();

  const fetchFn = async () => {
    if (!userId) throw new Error('userId is required');
    const result = (await getUserAiBudget(userId, accessToken)) as any;
    return result.data ?? result;
  };

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(fetchFn), [userId, accessToken]);

  return useQuery<AiUserBudget, FetchError>([queryNames.userAiBudget, userId], queryFn, {
    enabled: !!userId,
    ...queryOptions,
  });
}
